import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  collection, onSnapshot, doc, deleteDoc, query, orderBy,
} from 'firebase/firestore';
import { db } from '../firebase/firebaseConfig';
import './MembersPage.css'; // reuse table + dialog styles

const ALL = 'All';

const _months = ['Jan','Feb','Mar','Apr','May','Jun',
                 'Jul','Aug','Sep','Oct','Nov','Dec'];

function fmtWhen(ts) {
  if (!ts?.toDate) return '—';
  const d = ts.toDate();
  const now = new Date();
  const diffMs = now - d;
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1)    return 'just now';
  if (mins < 60)   return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24)    return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days === 1)  return 'yesterday';
  if (days < 7)    return `${days} days ago`;
  return `${d.getDate()} ${_months[d.getMonth()]} ${d.getFullYear()}`;
}

export default function NotificationsHistoryPage() {
  const [items,   setItems]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [search,  setSearch]  = useState('');
  const [typeF,   setTypeF]   = useState(ALL);
  const [toast,   setToast]   = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  useEffect(() => {
    const q = query(
      collection(db, 'notifications'),
      orderBy('sentAt', 'desc'),
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        setItems(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        setLoading(false);
      },
      () => setLoading(false),
    );
    return unsub;
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter(it => {
      const matchSearch = !q
        || it.title?.toLowerCase().includes(q)
        || it.body?.toLowerCase().includes(q)
        || it.target?.toLowerCase().includes(q);
      const matchType = typeF === ALL || (it.type ?? 'simple') === typeF.toLowerCase();
      return matchSearch && matchType;
    });
  }, [items, search, typeF]);

  const confirmDelete = useCallback(async () => {
    if (!deleteTarget) return;
    try {
      await deleteDoc(doc(db, 'notifications', deleteTarget.id));
      showToast('Notification removed from history.', 'success');
    } catch (e) {
      showToast(`Delete failed: ${e.message}`, 'error');
    } finally {
      setDeleteTarget(null);
    }
  }, [deleteTarget]);

  const showToast = (message, type) => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  const totalCount  = items.length;
  const promoCount  = items.filter(i => i.type === 'promo').length;
  const simpleCount = totalCount - promoCount;

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Sent History</h1>
          <p className="page-subtitle">
            {totalCount} total · {simpleCount} notifications · {promoCount} promos
          </p>
        </div>
      </div>

      {toast && (
        <div className={`mem-toast mem-toast--${toast.type}`}>
          {toast.message}
        </div>
      )}

      <div className="mem-filters">
        <input
          type="search"
          className="mem-search"
          placeholder="Search title, body, target…"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <select
          className="mem-select"
          value={typeF}
          onChange={e => setTypeF(e.target.value)}
        >
          {[ALL, 'Simple', 'Promo'].map(s => <option key={s}>{s}</option>)}
        </select>
      </div>

      {loading ? (
        <div className="mem-loading">
          <span className="tf-spinner tf-spinner--red" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="mem-empty">No items match this filter.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {filtered.map(it => (
            <HistoryCard
              key={it.id}
              item={it}
              onDelete={() => setDeleteTarget(it)}
            />
          ))}
        </div>
      )}

      {deleteTarget && (
        <div className="mem-dialog-overlay" onClick={() => setDeleteTarget(null)}>
          <div className="mem-dialog" onClick={e => e.stopPropagation()}>
            <h3 className="mem-dialog-title">Remove from history?</h3>
            <p className="mem-dialog-body">
              This removes the audit-log entry. Members who already received
              the push notification still see it on their phones — they only
              stop seeing it in the in-app Notifications list.
            </p>
            <div className="mem-dialog-actions">
              <button className="mem-dialog-cancel"
                onClick={() => setDeleteTarget(null)}>Cancel</button>
              <button className="mem-dialog-confirm"
                onClick={confirmDelete}>Remove</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function HistoryCard({ item, onDelete }) {
  const isPromo = item.type === 'promo';
  const hasImg  = !!item.imageUrl;
  const hasCta  = !!(item.ctaText && item.ctaUrl);

  return (
    <div style={{
      background: '#161618',
      border: '1px solid #1FD4B574',
      borderRadius: 12,
      padding: 14,
      display: 'flex',
      gap: 14,
    }}>
      {/* Thumbnail */}
      <div style={{ flexShrink: 0 }}>
        {hasImg ? (
          <img
            src={item.imageUrl}
            alt=""
            style={{
              width: 88, height: 88, borderRadius: 8,
              objectFit: 'cover', background: '#0F0F0F',
            }}
            onError={(e) => { e.currentTarget.style.display = 'none'; }}
          />
        ) : (
          <div style={{
            width: 88, height: 88, borderRadius: 8,
            background: '#0F0F0F',
            border: '1px solid #2A2A2A',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 30, color: '#D4B574',
          }}>
            {isPromo ? '★' : '✉'}
          </div>
        )}
      </div>

      {/* Body */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          display: 'flex',
          gap: 8,
          alignItems: 'center',
          marginBottom: 6,
          flexWrap: 'wrap',
        }}>
          <span style={{
            padding: '2px 8px',
            background: isPromo
              ? 'rgba(212, 181, 116, 0.18)'
              : 'rgba(34, 197, 94, 0.18)',
            color: isPromo ? '#D4B574' : '#22C55E',
            borderRadius: 4,
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: 1,
            textTransform: 'uppercase',
          }}>
            {isPromo ? 'Promo Popup' : 'Notification'}
          </span>
          <span style={{
            padding: '2px 8px',
            background: 'rgba(184, 159, 109, 0.12)',
            color: '#D8C8A4',
            borderRadius: 4,
            fontSize: 10,
            fontWeight: 600,
            letterSpacing: 0.5,
          }}>
            {item.target === 'all' ? '🌐 All members' : `📍 ${item.target}`}
          </span>
          <span style={{ color: '#B79F6D', fontSize: 11 }}>
            · {fmtWhen(item.sentAt)}
          </span>
        </div>

        {item.title && (
          <div style={{
            color: '#F7E8BF',
            fontSize: 15,
            fontWeight: 700,
            marginBottom: 3,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}>
            {item.title}
          </div>
        )}
        {item.body && (
          <div style={{
            color: '#D8C8A4',
            fontSize: 13,
            lineHeight: 1.45,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}>
            {item.body}
          </div>
        )}
        {!item.title && !item.body && (
          <div style={{ color: '#B79F6D', fontSize: 12, fontStyle: 'italic' }}>
            Image-only promo
          </div>
        )}
        {hasCta && (
          <div style={{
            marginTop: 8,
            display: 'flex', gap: 8, alignItems: 'center',
            color: '#D4B574', fontSize: 11,
          }}>
            <span style={{ fontWeight: 700 }}>Button:</span>
            <span style={{
              padding: '2px 8px',
              background: 'rgba(212, 181, 116, 0.12)',
              borderRadius: 4,
            }}>
              {item.ctaText}
            </span>
            <a href={item.ctaUrl} target="_blank" rel="noreferrer"
               style={{
                 color: '#B79F6D',
                 textDecoration: 'underline',
                 fontSize: 11,
                 overflow: 'hidden',
                 textOverflow: 'ellipsis',
                 whiteSpace: 'nowrap',
                 maxWidth: 280,
               }}>
              {item.ctaUrl}
            </a>
          </div>
        )}
      </div>

      {/* Delete */}
      <button
        className="mem-delete-btn"
        onClick={onDelete}
        title="Remove from history"
        style={{ flexShrink: 0, alignSelf: 'flex-start' }}
      >
        ✕
      </button>
    </div>
  );
}
